import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { PageHeader } from "~/components/page-header";
import { DataTable } from "~/components/data-table";
import { MasterDetail } from "~/components/master-detail";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { Checkbox } from "~/components/ui/checkbox";
import { useConfirm } from "~/components/confirm-dialog";
import { api, APIError, type ForwardingAddressInput } from "~/lib/api";
import { useMe, isAdmin } from "~/lib/auth";
import type { IForwardingAddress } from "~/lib/proto";

export const Route = createFileRoute("/_authed/ops/addresses")({
  component: Addresses,
});

type Selection = IForwardingAddress | "new" | null;

function Addresses() {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const [selected, setSelected] = useState<Selection>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["ops", "addresses"],
    queryFn: () => api.ops.listForwardingAddresses(),
  });

  const columns: ColumnDef<IForwardingAddress, unknown>[] = [
    { header: "Route", accessorKey: "originCountry", cell: ({ row }) => <span className="font-medium">{row.original.originCountry} · {row.original.mode}</span>, meta: { headClassName: "w-[120px]" } },
    { header: "Ship to", accessorKey: "shipTo", cell: ({ row }) => <span className="text-text-faint line-clamp-1">{row.original.shipTo}</span> },
    { header: "Active", accessorKey: "active", cell: ({ row }) => <Badge variant={row.original.active ? "success" : "secondary"}>{row.original.active ? "Active" : "Off"}</Badge>, meta: { headClassName: "w-[90px]" } },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Shipping addresses"
        caption="Customer-facing ship-to and warehouse contact per route"
        actions={admin ? <Button onClick={() => setSelected("new")}><Plus /> New route</Button> : undefined}
      />
      <MasterDetail
        open={selected !== null}
        onClose={() => setSelected(null)}
        detail={selected ? <AddressPanel row={selected === "new" ? null : selected} onClose={() => setSelected(null)} /> : null}
        emptyHint="Select a route to edit it"
        title="Route"
      >
        <DataTable
          data={rows}
          columns={columns}
          limit={rows?.length ?? 50}
          offset={0}
          onPageChange={() => {}}
          loading={isLoading}
          getRowId={(r) => String(r.id)}
          onRowClick={admin ? (r) => setSelected(r) : undefined}
          selectedRowId={selected && selected !== "new" ? String(selected.id) : undefined}
          emptyTitle="No routes yet"
          emptyBody={admin ? "Add a route so customers can register parcels." : "No routes configured."}
        />
      </MasterDetail>
    </>
  );
}

function AddressPanel({ row, onClose }: { row: IForwardingAddress | null; onClose: () => void }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const editing = !!row;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<ForwardingAddressInput>({ originCountry: "", mode: "air", shipTo: "", notes: "", warehouseEmail: "", active: true });

  useEffect(() => {
    if (row) setForm({ originCountry: row.originCountry ?? "", mode: row.mode ?? "air", shipTo: row.shipTo ?? "", notes: row.notes ?? "", warehouseEmail: row.warehouseEmail ?? "", active: row.active ?? true });
    else setForm({ originCountry: "", mode: "air", shipTo: "", notes: "", warehouseEmail: "", active: true });
  }, [row]);

  function set<K extends keyof ForwardingAddressInput>(k: K, v: ForwardingAddressInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    const ok = await confirm({
      title: editing ? "Save changes to this route?" : "Create this route?",
      body: "Customers see this ship-to as soon as you save.",
      confirmLabel: editing ? "Save changes" : "Create route",
    });
    if (!ok) return;
    setBusy(true);
    try {
      if (editing) await api.ops.updateForwardingAddress(String(row!.id), form);
      else await api.ops.createForwardingAddress(form);
      toast.success(editing ? "Route updated" : "Route created");
      void qc.invalidateQueries({ queryKey: ["ops", "addresses"] });
      onClose();
    } catch (e) {
      toast.error(e instanceof APIError ? e.message : "Could not save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="space-y-4 p-4 pr-10">
        <div>
          <p className="eyebrow">Route</p>
          <h2 className="section-heading mt-0.5">{editing ? `${row!.originCountry} · ${row!.mode}` : "New route"}</h2>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Origin country</Label><Input value={form.originCountry} disabled={editing} onChange={(e) => set("originCountry", e.target.value.toUpperCase())} placeholder="US" /></div>
          <div className="space-y-1.5"><Label>Mode</Label><Input value={form.mode} disabled={editing} onChange={(e) => set("mode", e.target.value.toLowerCase())} placeholder="air | sea" /></div>
        </div>
        <div className="space-y-1.5"><Label>Ship-to block</Label><Textarea rows={4} value={form.shipTo} onChange={(e) => set("shipTo", e.target.value)} placeholder="Warehouse address the customer pastes at checkout" /></div>
        <div className="space-y-1.5"><Label>Warehouse email</Label><Input value={form.warehouseEmail} onChange={(e) => set("warehouseEmail", e.target.value)} placeholder="KeBay email, notified with the tracking number on registration" /></div>
        <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Optional (VAT, fees, minimums…)" /></div>

        {editing && (
          <label className="flex items-center gap-2.5">
            <Checkbox checked={form.active} onCheckedChange={(v) => set("active", v === true)} />
            <span className="text-[13px]">Active (offered to customers)</span>
          </label>
        )}

        <Button className="w-full" loading={busy} onClick={() => void save()}>{editing ? "Save changes" : "Create route"}</Button>
      </div>
    </div>
  );
}
